import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { HttpRequestStatus, LoadingContainer } from "./loadingContainer";
import useReportRows from "../hooks/useReportRows";
import { Report, ReportRow } from "../api/models/report.models";
import useSupClasses from "../hooks/useSupClasses";
import { useShallow } from "zustand/shallow";
import { useUIStore } from "../stores/ui-store";
import { UIState } from "../types/ui-types";
import styled from "styled-components";
import { useForm, Controller } from "react-hook-form";
import ReportFooter from "./reportFooter";
import { InputNumber } from "primereact/inputnumber";
import { TabView, TabPanel } from "primereact/tabview";
import {
  CreateReportRowPayload,
  UpdateReportRowPayload,
} from "../api/models/reportRow.models";
import { RowValuesWithEcomodClass } from "../api/models/rowValues.models";
import { SupClass } from "../api/models/supClass.models";
import useEcomodClasses from "../hooks/useEcomodClasses";
import { Button } from "primereact/button";

// Simple type for SUP form data
interface SupFormData {
  plastic: {
    [materialCode: string]: {
      value: number | null;
      supPlasticWeight?: number | null;
      supReusablePlasticWeight?: number | null;
    };
  };
  paper: {
    [materialCode: string]: {
      value: number | null;
      supPlasticWeight?: number | null;
      supReusablePlasticWeight?: number | null;
    };
  };
  other: {
    [materialCode: string]: {
      value: number | null;
      supPlasticWeight?: number | null;
      supReusablePlasticWeight?: number | null;
    };
  };
}

const hasPositiveNumber = (value: number | null | undefined): boolean => {
  if (value === null || value === undefined) return false;

  return parseFloat(value.toString()) > 0;
};

const getNumericValue = (value: number | null | undefined): number => {
  if (value === null || value === undefined) return 0;

  return parseFloat(value.toString()) || 0;
};

const Container = styled.div`
  width: 100%;
  min-height: 80vh;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #ccc;
  margin: 1rem 0;
`;

const SectionDivider = styled.div`
  margin: 2rem 0 1.5rem 0;
  padding-top: 1.5rem;
  border-top: 1px solid #dee2e6;

  &:first-child {
    margin-top: 0;
    padding-top: 0;
    border-top: none;
  }
`;

const SectionHeader = styled.h4`
  font-size: 0.9rem;
  font-weight: 600;
  margin: 0 0 1rem 0;
  color: #495057;
`;

const PaddedContainer = styled.div`
  padding: 2rem;
`;

const SupFormContainer = styled.div`
  padding: 1rem;

  & h3 {
    margin-top: 2rem;
    margin-bottom: 2rem;
    font-weight: 500;
    font-size: 1.2rem;
  }

  & strong {
    font-weight: 500;
  }
`;

const Labels = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 8px 0;
  margin-right: 0.9rem;
  margin-top: 2rem;
  flex-grow: 1;
  max-width: 400px;

  @media (max-width: 1400px) {
    margin-top: 3rem;
    gap: 0.5rem;
  }

  > div {
    min-width: 250px;
    white-space: wrap;
    text-align: right;
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }
`;

const InputContainer = styled.div`
  font-size: 0.8rem;
  min-height: 50px;
  flex: 1;
`;

const LabelInputContainer = styled.div`
  font-size: 0.8rem;
  min-height: 51px;
  max-height: 51px;
`;

const Grid = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1rem;
  width: 100%;
  align-items: flex-start;
`;

const InputsGrid = styled.div<{ $hasBackground?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  padding: 8px 0;
  background: ${(props) => (props.$hasBackground ? "#f2f2f2" : "transparent")};
  padding: 12px;
  border-radius: var(--border-radius);
  min-width: 230px;
  max-width: 230px;

  & .p-inputnumber {
    width: 100%;
    max-width: 200px;
  }

  & .p-inputnumber-input {
    width: 100%;
    max-width: 200px;
  }
`;

const Header = styled.div<{ $span: number }>`
  font-weight: 400;
  font-size: 0.8rem;
  grid-column: span ${(props) => props.$span || 2};
  color: #797979;
`;

const MaterialsContainer = styled.div`
  margin-top: 1rem;
  margin-bottom: 2rem;
`;

interface SupFormProps {
  reportId?: number;
  reportData?: Report;
  isLoadingReport: boolean;
  handleNextStep: () => void;
  onFormDirtyChange?: (isDirty: boolean) => void;
  saveDraftRef?: React.MutableRefObject<(() => Promise<void>) | undefined>;
}

const BasicSupForm: React.FC<SupFormProps> = ({
  reportId,
  isLoadingReport,
  reportData,
  handleNextStep,
  onFormDirtyChange,
  saveDraftRef,
}: SupFormProps) => {
  const { t } = useTranslation();
  const [locale] = useUIStore(useShallow((state: UIState) => [state.locale]));
  const [activeTabIndex, setActiveTabIndex] = React.useState(0);

  // Something fucks up the data when customers move from this screen to summary too fast after saving
  // so we simulate a loading state for a few seconds to give backend time to process the changes
  const [isSimulatingLoad, setIsSimulatingLoad] = React.useState(false);

  const ecomodPaperCode = "1";
  const ecomodPlasticCode = "2";

  const amountOfPlasticOnPlasticTab = ["S1A2", "S3B2", "S3A2"];
  const amountOfPlasticOnPaperTab = ["S1A2", "S3B2", "S3A2"];
  const amountOfPlasticOnOtherTab = ["S1A2", "S3B2", "S3A2"];
  const reusablePlasticOnPlasticTab = ["S2A1", "S2A2"];
  const reusablePlasticOnPaperTab = ["S2A1", "S2A2"];
  const reusablePlasticOnOtherTab = ["S2A1", "S2A2"];

  const showOnlyInPlastic = ["S1A1", "S2A1", "S3A1", "S3B1"];

  const { supClasses, isLoading: isLoadingSupClasses } = useSupClasses();

  const { ecomodClasses, isLoading: isLoadingEcomodClasses } =
    useEcomodClasses();

  const {
    createReportRowData,
    isCreatingReportRow,
    updateReportRowData,
    isUpdatingReportRow,
  } = useReportRows();

  const {
    control,
    handleSubmit,
    formState: { isDirty, errors },
    getValues,
    watch,
    reset,
    trigger,
  } = useForm<SupFormData>({
    mode: "onBlur",
    defaultValues: {
      plastic: {},
      paper: {},
      other: {},
    },
  });

  useEffect(() => {
    // Notify parent of form dirty state changes
    if (onFormDirtyChange) {
      onFormDirtyChange(isDirty);
    }
  }, [isDirty, onFormDirtyChange]);

  const isLoading =
    isLoadingReport || isLoadingSupClasses || isLoadingEcomodClasses;

  const validateSupSideWeight = (
    fieldValue: number | null | undefined,
    totalWeight: number | null | undefined,
    isRequired: boolean,
  ) => {
    if (isRequired && !hasPositiveNumber(fieldValue)) {
      return t("label-required");
    }

    if (getNumericValue(fieldValue) > getNumericValue(totalWeight)) {
      return t("sup-related-weight-exceeds-total-validation-error");
    }

    return true;
  };

  // Helper function to get the label based on locale
  const getSupClassLabel = (supClass: SupClass): string => {
    switch (locale) {
      case "fi":
        return supClass.nameFi || supClass.code || "";
      case "swe":
        return supClass.nameSv || supClass.code || "";
      case "en":
      default:
        return supClass.nameEn || supClass.code || "";
    }
  };

  // Helper function to categorize SUP classes
  const categorizeSupClasses = () => {
    if (!supClasses) return { plastic: [], paper: [], other: [] };

    // For SUP reporting, all SUP classes can be used with different material types (plastic/paper/other)
    // Materials in showOnlyInPlastic should only appear in the plastic tab
    return {
      plastic: supClasses || [],
      paper:
        supClasses?.filter(
          (cls) => !showOnlyInPlastic.includes(cls.code || ""),
        ) || [],
      other:
        supClasses?.filter(
          (cls) => !showOnlyInPlastic.includes(cls.code || ""),
        ) || [],
    };
  };

  useEffect(() => {
    if (reportData?.rows && reportData.rows.length > 0 && supClasses) {
      const firstRow = reportData.rows[0];

      const plasticValues: {
        [materialCode: string]: {
          value: number | null;
          supPlasticWeight?: number | null;
          supReusablePlasticWeight?: number | null;
        };
      } = {};
      const paperValues: {
        [materialCode: string]: {
          value: number | null;
          supPlasticWeight?: number | null;
          supReusablePlasticWeight?: number | null;
        };
      } = {};
      const otherValues: {
        [materialCode: string]: {
          value: number | null;
          supPlasticWeight?: number | null;
          supReusablePlasticWeight?: number | null;
        };
      } = {};

      // Extract values from firstRow and populate the form fields
      if (firstRow.values) {
        firstRow.values.forEach((rowValue) => {
          const materialCode = rowValue.supClass?.code;
          if (materialCode) {
            const value = rowValue.value || null;
            const ecomodCode = rowValue.ecoModulationClass?.code;

            // Use ecoModulationClass.code to determine category
            if (ecomodCode === ecomodPlasticCode) {
              // Plastic section
              plasticValues[materialCode] = {
                value,
                supPlasticWeight: rowValue.supPlasticWeight || null,
                supReusablePlasticWeight:
                  rowValue.supReusablePlasticWeight || null,
              };
            } else if (ecomodCode === ecomodPaperCode) {
              // Paper section
              paperValues[materialCode] = {
                value,
                supPlasticWeight: rowValue.supPlasticWeight || null,
                supReusablePlasticWeight:
                  rowValue.supReusablePlasticWeight || null,
              };
            } else {
              // Other section (no ecoModulationClass or different code)
              otherValues[materialCode] = {
                value,
                supPlasticWeight: rowValue.supPlasticWeight || null,
                supReusablePlasticWeight:
                  rowValue.supReusablePlasticWeight || null,
              };
            }
          }
        });
      }

      reset({
        plastic: plasticValues,
        paper: paperValues,
        other: otherValues,
      });
    }
  }, [reportData, supClasses, reset, locale]);

  const findMatchingIdFromReportData = (
    reportData: Report | undefined,
    supClassCode: string,
    ecomodClassCode?: string,
  ): number | undefined => {
    if (!reportData?.rows || reportData.rows.length === 0) return undefined;

    for (const row of reportData.rows) {
      if (row.values) {
        for (const value of row.values) {
          if (
            value.supClass?.code === supClassCode &&
            value.ecoModulationClass?.code === ecomodClassCode &&
            value.id
          ) {
            return value.id;
          }
        }
      }
    }

    return undefined;
  };

  const generateRowFromData = (
    data: SupFormData,
  ): RowValuesWithEcomodClass[] => {
    let rows: RowValuesWithEcomodClass[] = [];

    // Populate plastic values
    for (const [materialCode, value] of Object.entries(data.plastic)) {
      const hasValue =
        value.value !== null &&
        value.value !== undefined &&
        parseFloat(value.value.toString()) > 0;
      const hasSupPlasticWeight =
        value.supPlasticWeight !== null &&
        value.supPlasticWeight !== undefined &&
        parseFloat(value.supPlasticWeight.toString()) > 0;
      const hasSupReusablePlasticWeight =
        value.supReusablePlasticWeight !== null &&
        value.supReusablePlasticWeight !== undefined &&
        parseFloat(value.supReusablePlasticWeight.toString()) > 0;

      if (
        !!value &&
        (hasValue || hasSupPlasticWeight || hasSupReusablePlasticWeight)
      ) {
        const supClass = supClasses?.find((cls) => cls.code === materialCode);
        // Plastic section - set ecoModulationClass with plastic code
        const ecomodClass = ecomodClasses?.find(
          (ecomod) => ecomod.code === ecomodPlasticCode,
        );

        rows.push({
          id: findMatchingIdFromReportData(
            reportData,
            supClass?.code || "",
            ecomodClass?.code,
          ),
          ecoModulationClass: ecomodClass,
          supClass: supClass,
          businessUse: false,
          reusableClass: undefined,
          packagedAbroad: false,
          value: value.value || 0,
          supPlasticWeight: value.supPlasticWeight || undefined,
          supReusablePlasticWeight: value.supReusablePlasticWeight || undefined,
          secMaterial: null,
          secValue: 0,
          secValueType: undefined,
          isEmptyCup: false,
        });
      }
    }

    // Populate paper values
    for (const [materialCode, value] of Object.entries(data.paper)) {
      const hasValue =
        value.value !== null &&
        value.value !== undefined &&
        parseFloat(value.value.toString()) > 0;
      const hasSupPlasticWeight =
        value.supPlasticWeight !== null &&
        value.supPlasticWeight !== undefined &&
        parseFloat(value.supPlasticWeight.toString()) > 0;
      const hasSupReusablePlasticWeight =
        value.supReusablePlasticWeight !== null &&
        value.supReusablePlasticWeight !== undefined &&
        parseFloat(value.supReusablePlasticWeight.toString()) > 0;

      if (
        !!value &&
        (hasValue || hasSupPlasticWeight || hasSupReusablePlasticWeight)
      ) {
        const supClass = supClasses?.find((cls) => cls.code === materialCode);
        // Paper section - set ecoModulationClass with paper code
        const ecomodClass = ecomodClasses?.find(
          (ecomod) => ecomod.code === ecomodPaperCode,
        );

        rows.push({
          id: findMatchingIdFromReportData(
            reportData,
            supClass?.code || "",
            ecomodClass?.code,
          ),
          ecoModulationClass: ecomodClass,
          supClass: supClass,
          businessUse: false,
          reusableClass: undefined,
          packagedAbroad: false,
          value: value.value || 0,
          supPlasticWeight: value.supPlasticWeight || undefined,
          supReusablePlasticWeight: value.supReusablePlasticWeight || undefined,
          secMaterial: null,
          secValue: 0,
          secValueType: undefined,
          isEmptyCup: false,
        });
      }
    }

    // Populate other values
    for (const [materialCode, value] of Object.entries(data.other)) {
      const hasValue =
        value.value !== null &&
        value.value !== undefined &&
        parseFloat(value.value.toString()) > 0;
      const hasSupPlasticWeight =
        value.supPlasticWeight !== null &&
        value.supPlasticWeight !== undefined &&
        parseFloat(value.supPlasticWeight.toString()) > 0;
      const hasSupReusablePlasticWeight =
        value.supReusablePlasticWeight !== null &&
        value.supReusablePlasticWeight !== undefined &&
        parseFloat(value.supReusablePlasticWeight.toString()) > 0;

      if (
        !!value &&
        (hasValue || hasSupPlasticWeight || hasSupReusablePlasticWeight)
      ) {
        const supClass = supClasses?.find((cls) => cls.code === materialCode);
        // Other section - no ecoModulationClass

        rows.push({
          id: findMatchingIdFromReportData(
            reportData,
            supClass?.code || "",
            undefined,
          ),
          ecoModulationClass: undefined,
          supClass: supClass,
          businessUse: false,
          reusableClass: undefined,
          packagedAbroad: false,
          value: value.value || 0,
          supPlasticWeight: value.supPlasticWeight || undefined,
          supReusablePlasticWeight: value.supReusablePlasticWeight || undefined,
          secMaterial: null,
          secValue: 0,
          secValueType: undefined,
          isEmptyCup: false,
        });
      }
    }

    return rows;
  };

  const createReportRowFromFormData = async (
    data: SupFormData,
  ): Promise<ReportRow[] | void> => {
    if (reportData?.rows && reportData.rows.length > 0) {
      const firstRow = reportData.rows[0];

      const rowPayload: UpdateReportRowPayload = {
        id: firstRow.id as number,
        reportId: reportId as number,
        ean: firstRow.ean,
        name: firstRow.name,
        amount: firstRow.amount,
        active: firstRow.active,
        values: reportData.rows?.length
          ? reportData.rows[0].values
              .filter((val) => !val.supClass) // Preserving non-SUP class values
              .concat(generateRowFromData(data))
          : generateRowFromData(data),
      };

      const result = await updateReportRowData(rowPayload);
      return [result];
    } else {
      const rowPayload: CreateReportRowPayload = {
        reportId: reportId as number,
        ean: "",
        name: "Generic SUP Material Row",
        amount: 1,
        active: true,
        values: reportData?.rows?.length
          ? reportData.rows[0].values.concat(generateRowFromData(data))
          : generateRowFromData(data),
      };

      const result = await createReportRowData(rowPayload);
      return [result];
    }
  };

  const handleUpdateReport = async (data: SupFormData) => {
    if (!reportId) return;

    await createReportRowFromFormData(data);

    // Simulate 5 second loading before moving to summary
    setIsSimulatingLoad(true);
    setTimeout(() => {
      setIsSimulatingLoad(false);
      handleNextStep();
    }, 1500);
  };

  const handleNextStepClick = () => {
    handleSubmit(handleUpdateReport)();
  };

  const handleNextMaterial = () => {
    if (activeTabIndex < 2) {
      setActiveTabIndex(activeTabIndex + 1);
    }
  };

  const { plastic, paper, other } = categorizeSupClasses();

  // Helper function to group SUP classes by sections
  const groupSupClassesBySections = (materials: SupClass[]) => {
    const sections = [
      {
        title: t("header-sup-food-packaging"),
        codes: ["S1A1", "S1A2"],
        materials: [] as SupClass[],
      },
      {
        title: t("header-sup-flexible-food-packaging"),
        codes: ["S1B1"],
        materials: [] as SupClass[],
      },
      {
        title: t("header-sup-beverage-packaging"),
        codes: ["S2A1", "S2A2", "S2A3"],
        materials: [] as SupClass[],
      },
      {
        title: t("header-sup-beverage-cups"),
        codes: ["S3A1", "S3A2", "S3B1", "S3B2"],
        materials: [] as SupClass[],
      },
      {
        title: t("header-sup-lightweight-carrier-bags"),
        codes: ["S4A1"],
        materials: [] as SupClass[],
      },
    ];

    materials.forEach((material) => {
      if (material.code) {
        const section = sections.find((s) => s.codes.includes(material.code!));
        if (section) {
          section.materials.push(material);
        }
      }
    });

    return sections.filter((section) => section.materials.length > 0);
  };

  const handleSaveDraft = async () => {
    if (!reportId) return;

    await createReportRowFromFormData(getValues());
  };

  // Expose save draft function to parent through ref
  useEffect(() => {
    if (saveDraftRef) {
      saveDraftRef.current = handleSaveDraft;
    }
  }, [saveDraftRef, handleSaveDraft]);

  // Helper function to render materials grid for SUP reporting
  const renderSupMaterialsGrid = (
    category: "plastic" | "paper" | "other",
    materials: SupClass[],
  ) => {
    if (!materials || materials.length === 0) return null;

    const sections = groupSupClassesBySections(materials);

    // Determine which SUP classes should show plastic weight and reusable plastic weight inputs
    let amountOfPlasticCodes: string[] = [];
    let reusablePlasticCodes: string[] = [];

    if (category === "plastic") {
      amountOfPlasticCodes = amountOfPlasticOnPlasticTab;
      reusablePlasticCodes = reusablePlasticOnPlasticTab;
    } else if (category === "paper") {
      amountOfPlasticCodes = amountOfPlasticOnPaperTab;
      reusablePlasticCodes = reusablePlasticOnPaperTab;
    } else if (category === "other") {
      amountOfPlasticCodes = amountOfPlasticOnOtherTab;
      reusablePlasticCodes = reusablePlasticOnOtherTab;
    }

    return (
      <>
        {sections.map((section, sectionIndex) => {
          // Check if any material in this section should show plastic weight or reusable plastic weight
          const hasVisiblePlasticWeight = section.materials.some((material) =>
            amountOfPlasticCodes.includes(material.code || ""),
          );
          const hasVisibleReusablePlasticWeight = section.materials.some(
            (material) => reusablePlasticCodes.includes(material.code || ""),
          );

          return (
            <SectionDivider key={section.title}>
              {/* <SectionHeader className="section-header">
              {section.title}
             </SectionHeader> */}
              <Grid>
                {/* Labels column */}
                <Labels>
                  {section.materials.map((material) => (
                    <LabelInputContainer key={material.code}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-end",
                        }}
                      >
                        <span>{getSupClassLabel(material)}</span>
                      </div>
                    </LabelInputContainer>
                  ))}
                </Labels>

                {/* Amount column */}
                <InputsGrid $hasBackground>
                  <Header $span={1}>
                    {t("header-light-report-amount-kg")}
                  </Header>

                  {section.materials.map((material) =>
                    (() => {
                      const plasticWeightFieldName =
                        `${category}.${material.code}.supPlasticWeight` as any;
                      const reusablePlasticWeightFieldName =
                        `${category}.${material.code}.supReusablePlasticWeight` as any;

                      return (
                        <InputContainer
                          key={material.code}
                          className="input-container"
                        >
                          <Controller
                            name={`${category}.${material.code}.value` as any}
                            control={control}
                            defaultValue={null}
                            render={({ field }) => (
                              <InputNumber
                                {...field}
                                onChange={(e) => {
                                  field.onChange(e.value);
                                  void trigger([
                                    plasticWeightFieldName,
                                    reusablePlasticWeightFieldName,
                                  ]);
                                }}
                                value={field.value}
                                suffix=" kg"
                                placeholder="0"
                                locale="fi-FI"
                                useGrouping={false}
                              />
                            )}
                          />
                        </InputContainer>
                      );
                    })(),
                  )}
                </InputsGrid>

                {/* SUP Plastic Weight column */}
                <InputsGrid>
                  <Header
                    $span={1}
                    style={{
                      visibility: hasVisiblePlasticWeight
                        ? "visible"
                        : "hidden",
                    }}
                  >
                    {t("header-sup-plastic-weight-kg")}
                  </Header>

                  {section.materials.map((material) =>
                    (() => {
                      const totalWeight = watch(
                        `${category}.${material.code}.value` as any,
                      ) as number | null | undefined;
                      const shouldRequirePlasticWeight =
                        amountOfPlasticCodes.includes(material.code || "") &&
                        hasPositiveNumber(totalWeight);

                      return (
                        <InputContainer
                          key={`${material.code}-plastic`}
                          className="input-container"
                          style={{
                            visibility: amountOfPlasticCodes.includes(
                              material.code || "",
                            )
                              ? "visible"
                              : "hidden",
                          }}
                        >
                          <Controller
                            name={
                              `${category}.${material.code}.supPlasticWeight` as any
                            }
                            control={control}
                            defaultValue={null}
                            rules={{
                              validate: (fieldValue) =>
                                validateSupSideWeight(
                                  fieldValue,
                                  totalWeight,
                                  shouldRequirePlasticWeight,
                                ),
                            }}
                            render={({ field, fieldState }) => (
                              <InputNumber
                                {...field}
                                onChange={(e) => {
                                  field.onChange(e.value);
                                }}
                                value={field.value}
                                suffix=" kg"
                                placeholder="0"
                                locale="fi-FI"
                                useGrouping={false}
                                invalid={fieldState.invalid}
                                tooltip={fieldState.error?.message}
                              />
                            )}
                          />
                        </InputContainer>
                      );
                    })(),
                  )}
                </InputsGrid>

                {/* SUP Reusable Plastic Weight column */}
                <InputsGrid $hasBackground>
                  <Header
                    $span={1}
                    style={{
                      visibility: hasVisibleReusablePlasticWeight
                        ? "visible"
                        : "hidden",
                    }}
                  >
                    {t("header-sup-reusable-plastic-weight-kg")}
                  </Header>

                  {section.materials.map((material) =>
                    (() => {
                      const totalWeight = watch(
                        `${category}.${material.code}.value` as any,
                      ) as number | null | undefined;
                      const shouldRequireReusablePlasticWeight =
                        reusablePlasticCodes.includes(material.code || "") &&
                        hasPositiveNumber(totalWeight);

                      return (
                        <InputContainer
                          key={`${material.code}-reusable`}
                          className="input-container"
                          style={{
                            visibility: reusablePlasticCodes.includes(
                              material.code || "",
                            )
                              ? "visible"
                              : "hidden",
                          }}
                        >
                          <Controller
                            name={
                              `${category}.${material.code}.supReusablePlasticWeight` as any
                            }
                            control={control}
                            defaultValue={null}
                            rules={{
                              validate: (fieldValue) =>
                                validateSupSideWeight(
                                  fieldValue,
                                  totalWeight,
                                  shouldRequireReusablePlasticWeight,
                                ),
                            }}
                            render={({ field, fieldState }) => (
                              <InputNumber
                                {...field}
                                onChange={(e) => {
                                  field.onChange(e.value);
                                }}
                                value={field.value}
                                suffix=" kg"
                                placeholder="0"
                                locale="fi-FI"
                                useGrouping={false}
                                invalid={fieldState.invalid}
                                tooltip={fieldState.error?.message}
                              />
                            )}
                          />
                        </InputContainer>
                      );
                    })(),
                  )}
                </InputsGrid>
              </Grid>
            </SectionDivider>
          );
        })}
      </>
    );
  };

  return (
    <Container className="sup-container">
      <LoadingContainer
        status={
          isLoading ? HttpRequestStatus.LOADING : HttpRequestStatus.SUCCESS
        }
      >
        <PaddedContainer>
          <SupFormContainer>
            <form>
              <TabView
                activeIndex={activeTabIndex}
                onTabChange={(e) => setActiveTabIndex(e.index)}
              >
                {/* Plastic Tab */}
                {plastic.length > 0 && (
                  <TabPanel header={t("header-plastic-materials")}>
                    <MaterialsContainer>
                      {renderSupMaterialsGrid("plastic", plastic)}
                    </MaterialsContainer>
                  </TabPanel>
                )}

                {/* Paper Tab */}
                {paper.length > 0 && (
                  <TabPanel header={t("header-paper-materials")}>
                    <MaterialsContainer>
                      {renderSupMaterialsGrid("paper", paper)}
                    </MaterialsContainer>
                  </TabPanel>
                )}

                {/* Other Materials Tab */}
                {other.length > 0 && (
                  <TabPanel header={t("header-other-materials")}>
                    <MaterialsContainer>
                      {renderSupMaterialsGrid("other", other)}
                    </MaterialsContainer>
                  </TabPanel>
                )}
              </TabView>
            </form>
          </SupFormContainer>
        </PaddedContainer>
        <ReportFooter
          saveDraft={handleSaveDraft}
          handleNextStep={handleNextStepClick}
          nextStepLabel={t("cmd-next")}
          proceedDisabled={false}
          isLoading={
            isCreatingReportRow || isUpdatingReportRow || isSimulatingLoad
          }
          secondaryAction={
            <Button
              label={t("cmd-next-material")}
              onClick={(e) => {
                e.stopPropagation();
                handleNextMaterial();
              }}
              disabled={activeTabIndex >= 2}
            />
          }
        />
      </LoadingContainer>
    </Container>
  );
};

export default BasicSupForm;
